import type {
  AccountingFrame,
  GameStateSnapshot,
  RankExpPoint,
  RankSubmissionDraft,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'

export const RANK_MINIMUM_DURATION_MS = 3_600_000
export const RANK_MAP_FRESHNESS_MS = 15_000
export const RANK_RECOGNITION_GRACE_MS = 120_000

interface ExpAccumulator {
  start: RankExpPoint | null
  end: RankExpPoint | null
  last: RankExpPoint | null
  gain: number
  trustedDurationMs: number
}

interface MesosAccumulator {
  startValue: number | null
  endValue: number | null
  lastValue: number | null
  gain: number
  trustedDurationMs: number
}

interface ActiveSegment {
  mapName: string
  levelBucket: number
  startedAt: number
  lastContinuousAt: number
  lastMapObservedAt: number
  recognitionPaused: boolean
  captureGeneration: number
  exp: ExpAccumulator
  mesos: MesosAccumulator
}

const emptyExp = (): ExpAccumulator => ({ start: null, end: null, last: null, gain: 0, trustedDurationMs: 0 })
const emptyMesos = (): MesosAccumulator => ({ startValue: null, endValue: null, lastValue: null, gain: 0, trustedDurationMs: 0 })

export function rankLevelBucket(level: number): number {
  return Math.floor(level / 5) * 5
}

export function normalizeRankMapName(value: string): string {
  return value
    .normalize('NFKC')
    .replace(/[\u0000-\u001f\u007f-\u009f]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
}

export class EfficiencySegmentTracker {
  private segment: ActiveSegment | null = null

  constructor(private readonly preset: GamePreset) {}

  reset(): void {
    this.segment = null
  }

  ingest(state: GameStateSnapshot, accounting: AccountingFrame): RankSubmissionDraft | null {
    const mapName = state.mapName ? normalizeRankMapName(state.mapName) : ''
    const mapObservedAt = state.observedAt?.mapName ?? null
    const levelBucket = state.level === null ? null : rankLevelBucket(state.level)

    if (!this.segment) {
      if (!this.mapIsFresh(state.capturedAt, mapName, mapObservedAt) || levelBucket === null) return null
      this.startSegment(state, accounting, mapName, levelBucket, mapObservedAt!)
      this.observeAccounting(accounting)
      return null
    }

    const segment = this.segment
    if (levelBucket === null) return this.finish(segment.lastContinuousAt)

    const recognitionGapMs = Math.max(0, state.capturedAt - segment.lastMapObservedAt)
    if (recognitionGapMs > RANK_RECOGNITION_GRACE_MS) {
      const completed = this.finish(segment.lastContinuousAt)
      if (this.mapIsFresh(state.capturedAt, mapName, mapObservedAt)) {
        this.startSegment(state, accounting, mapName, levelBucket, mapObservedAt!)
        this.observeAccounting(accounting)
      }
      return completed
    }

    const hasNewMapObservation = mapObservedAt !== null && mapObservedAt > segment.lastMapObservedAt
    if ((hasNewMapObservation && mapName !== segment.mapName) || levelBucket !== segment.levelBucket) {
      const completed = this.finish(segment.lastContinuousAt)
      if (this.mapIsFresh(state.capturedAt, mapName, mapObservedAt)) {
        this.startSegment(state, accounting, mapName, levelBucket, mapObservedAt!)
        this.observeAccounting(accounting)
      }
      return completed
    }

    if (hasNewMapObservation && mapName === segment.mapName) {
      segment.lastMapObservedAt = mapObservedAt
      this.resumeRecognition(segment)
    }

    const mapAgeMs = Math.max(0, state.capturedAt - segment.lastMapObservedAt)
    if (!mapName || mapName !== segment.mapName || mapAgeMs > RANK_MAP_FRESHNESS_MS) {
      this.pauseRecognition(segment)
      return null
    }

    segment.lastContinuousAt = state.capturedAt
    this.observeAccounting(accounting)
    return null
  }

  finish(now = Date.now()): RankSubmissionDraft | null {
    const segment = this.segment
    this.segment = null
    if (!segment?.exp.start || !segment.exp.end || segment.exp.trustedDurationMs < RANK_MINIMUM_DURATION_MS) return null

    const endedAt = Math.max(segment.startedAt, Math.min(now, segment.lastContinuousAt))
    const mesos = segment.mesos
    return {
      submissionId: crypto.randomUUID(),
      presetId: this.preset.id,
      presetVersion: this.preset.version,
      mapName: segment.mapName,
      levelBucket: segment.levelBucket,
      startedAt: segment.startedAt,
      endedAt,
      exp: {
        gain: segment.exp.gain,
        trustedDurationMs: segment.exp.trustedDurationMs,
        start: segment.exp.start,
        end: segment.exp.end,
      },
      ...(mesos.startValue !== null
        && mesos.endValue !== null
        && mesos.gain >= 0
        && mesos.trustedDurationMs >= RANK_MINIMUM_DURATION_MS
        ? {
            mesos: {
              gain: mesos.gain,
              trustedDurationMs: mesos.trustedDurationMs,
              startValue: mesos.startValue,
              endValue: mesos.endValue,
            },
          }
        : {}),
    }
  }

  private observeExp(accounting: AccountingFrame): void {
    if (!this.segment) return
    const update = accounting.exp
    const metric = this.segment.exp
    if (update.continuity === 'ignored' || update.continuity === 'pending') return
    if (update.continuity === 'unavailable' || update.continuity === 'discontinuous') {
      metric.last = update.end
      if (update.end) {
        metric.start ??= update.end
        metric.end = update.end
      }
      return
    }
    if (!update.exact || !update.end) return
    if (!metric.last || update.continuity === 'baseline' || !update.start) {
      metric.start ??= update.end
      metric.end = update.end
      metric.last = update.end
      return
    }
    if (!this.sameExpPoint(metric.last, update.start) || update.delta === null || update.delta < 0) {
      metric.last = update.end
      metric.end = update.end
      return
    }
    metric.gain += update.delta
    metric.trustedDurationMs += Math.max(0, update.trustedIntervalMs)
    metric.last = update.end
    metric.end = update.end
  }

  private startSegment(
    state: GameStateSnapshot,
    accounting: AccountingFrame,
    mapName: string,
    levelBucket: number,
    mapObservedAt: number,
  ): void {
    this.segment = {
      mapName,
      levelBucket,
      startedAt: state.capturedAt,
      lastContinuousAt: state.capturedAt,
      lastMapObservedAt: mapObservedAt,
      recognitionPaused: false,
      captureGeneration: accounting.captureGeneration,
      exp: emptyExp(),
      mesos: emptyMesos(),
    }
  }

  private observeAccounting(accounting: AccountingFrame): void {
    if (!this.segment) return
    if (this.segment.captureGeneration !== accounting.captureGeneration) {
      this.segment.captureGeneration = accounting.captureGeneration
      this.resetMetricContinuity(this.segment)
    }
    this.observeExp(accounting)
    this.observeMesos(accounting)
  }

  private pauseRecognition(segment: ActiveSegment): void {
    if (segment.recognitionPaused) return
    segment.recognitionPaused = true
    this.resetMetricContinuity(segment)
  }

  private resumeRecognition(segment: ActiveSegment): void {
    if (!segment.recognitionPaused) return
    segment.recognitionPaused = false
    this.resetMetricContinuity(segment)
  }

  private resetMetricContinuity(segment: ActiveSegment): void {
    segment.exp.last = null
    segment.mesos.lastValue = null
  }

  private mapIsFresh(capturedAt: number, mapName: string, mapObservedAt: number | null): boolean {
    return Boolean(mapName && mapObservedAt !== null && capturedAt - mapObservedAt <= RANK_MAP_FRESHNESS_MS)
  }

  private observeMesos(accounting: AccountingFrame): void {
    if (!this.segment) return
    const update = accounting.mesos
    const metric = this.segment.mesos
    if (update.continuity === 'ignored' || update.continuity === 'pending') return
    if (update.continuity === 'unavailable' || update.continuity === 'discontinuous') {
      metric.lastValue = update.endValue
      if (update.endValue !== null) {
        metric.startValue ??= update.endValue
        metric.endValue = update.endValue
      }
      return
    }
    if (update.endValue === null) return
    if (metric.lastValue === null || update.continuity === 'baseline' || update.startValue === null) {
      metric.startValue ??= update.endValue
      metric.endValue = update.endValue
      metric.lastValue = update.endValue
      return
    }
    if (metric.lastValue !== update.startValue || update.delta === null) {
      metric.lastValue = update.endValue
      metric.endValue = update.endValue
      return
    }
    if (update.delta >= 0) {
      metric.gain += update.delta
      metric.trustedDurationMs += Math.max(0, update.trustedIntervalMs)
    }
    metric.lastValue = update.endValue
    metric.endValue = update.endValue
  }

  private sameExpPoint(left: RankExpPoint, right: RankExpPoint): boolean {
    return left.level === right.level && left.absolute === right.absolute
  }
}
